import React, { useState, useMemo } from 'react';
import useSimulationStore from '../../store/simulationStore'; // Path from src/components/ui/ to src/store/
import { ReactionDefinition, ReactionComponent } from '../../types/simulation'; // Path from src/components/ui/ to src/types/
import { ActiveReactionConfig } from '../../store/simulationStore';
import { Reaction, Species } from '../../types/chemistry';
import { shallow } from 'zustand/shallow';

// Helper to format reaction components (can be moved to utils later)
const formatComponentsToString = (components: ReactionComponent[]): string => {
  if (!components || components.length === 0) return '';
  return components.map(c => `${c.count}${c.name}`).join(' + ');
};

// Format a single reaction into a readable string
const formatReaction = (reaction: ReactionDefinition): string => {
  const reactantsStr = formatComponentsToString(reaction.reactants);
  const productsStr = formatComponentsToString(reaction.products);
  const probabilityStr = reaction.probability !== undefined ? `P=${reaction.probability.toFixed(2)}` : '';
  const rateStr = reaction.rateConstant !== undefined ? `K=${reaction.rateConstant}` : '';
  const details = [probabilityStr, rateStr].filter(Boolean).join(', '); // Join details with comma if both exist

  return `${reactantsStr} -> ${productsStr}${details ? ` (${details})` : ''}`;
};

const ActiveReactionsDisplay: React.FC = () => {
  // Select the necessary state for translation
  const {
    activeConfigReactions,
    availableReactions,
    availableSpecies
  } = useSimulationStore(state => ({
    activeConfigReactions: state.activeChemistryConfig.reactions,
    availableReactions: state.availableReactions,
    availableSpecies: state.availableSpecies
  }), shallow); // Use shallow comparison

  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  // Memoize the translated reactions to avoid recalculating on every render
  const displayedReactions: ReactionDefinition[] = useMemo(() => {
    // Debug logging disabled
    // console.log("[ActiveReactionsDisplay] Translating active config for display...");
    if (!activeConfigReactions || activeConfigReactions.length === 0 || !availableReactions || !availableSpecies) {
      // console.log("[ActiveReactionsDisplay] Missing data for translation, returning empty.");
      return [];
    }

    const speciesIdToNameMap = new Map<string, string>();
    availableSpecies.forEach(s => speciesIdToNameMap.set(s.id, s.name));

    const mappedReactions: (ReactionDefinition | null)[] = activeConfigReactions.map((activeReaction: ActiveReactionConfig) => {
      const baseReaction = availableReactions.find(r => r.id === activeReaction.reactionDefId);
      if (!baseReaction) return null;

      // Debug logging disabled
      // console.log(`[ActiveReactionsDisplay] Processing reaction ${activeReaction.reactionDefId}:`, {
      //   reactantsCoeff: activeReaction.reactantsCoeff,
      //   productsCoeff: activeReaction.productsCoeff
      // });

      const mapCoeffToLegacy = (coeffs: { [speciesId: string]: number }): ReactionComponent[] => {
        return Object.entries(coeffs).map(([speciesId, count]) => {
          const name = speciesIdToNameMap.get(speciesId);
          // Use ID as fallback name if not found, but log warning
          if (!name) console.warn(`[ActiveReactionsDisplay] Species name not found for ID ${speciesId}`);
          return { name: name || speciesId, count };
        }).filter(comp => comp.count > 0);
      };

      const name = baseReaction.description || baseReaction.id;
      const probability = baseReaction.probability ?? 0;

      return {
        id: activeReaction.id,
        name: name,
        reactants: mapCoeffToLegacy(activeReaction.reactantsCoeff),
        products: mapCoeffToLegacy(activeReaction.productsCoeff),
        probability: probability,
      };
    });

    // Filter out nulls and assert the correct type
    const filteredReactions: ReactionDefinition[] = mappedReactions.filter((r): r is ReactionDefinition => r !== null);
    // console.log("[ActiveReactionsDisplay] Translation complete. Reactions to display:", filteredReactions);
    return filteredReactions;

    // Dependency array includes the raw data used for translation
  }, [activeConfigReactions, availableReactions, availableSpecies]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle} onClick={toggleExpansion}>
        <span>Active Reactions</span>
        <button style={buttonStyle}>{isExpanded ? '-' : '+'}</button>
      </div>

      {isExpanded && (
        <div style={contentStyle}>
          {/* Use the memoized displayedReactions */}
          {displayedReactions.length === 0 ? (
            <span style={placeholderStyle}>No reactions configured.</span>
          ) : (
            <ul>
              {displayedReactions.map((reaction) => (
                // Use reaction.id which should be the unique ActiveReactionConfig id
                <li key={reaction.id} style={listItemStyle}>
                  {formatReaction(reaction)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// Basic Inline Styles (adjust as needed)
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '10px',
  left: '10px', // Position bottom-left
  width: 'auto',
  minWidth: '250px',
  maxWidth: '45vw',
  maxHeight: '200px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: '#eee',
  border: '1px solid #555',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'monospace',
  zIndex: 100,
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: 'rgba(60, 60, 60, 0.9)',
  fontWeight: 'bold',
  flexShrink: 0,
  borderBottom: '1px solid #555',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const contentStyle: React.CSSProperties = {
  overflowY: 'auto',
  padding: '4px 8px',
  flexGrow: 1,
  minHeight: 0,
};

const buttonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #888',
  color: '#eee',
  padding: '0px 4px',
  marginLeft: '8px',
  cursor: 'pointer',
  fontSize: '10px',
  lineHeight: '1',
};

const placeholderStyle: React.CSSProperties = {
  fontStyle: 'italic',
  color: '#aaa',
};

const listItemStyle: React.CSSProperties = {
  marginBottom: '2px',
  whiteSpace: 'nowrap',
  listStyleType: 'none',
};

export default ActiveReactionsDisplay; 